"use client";
import { AuthProvider, AuthProviderProps, useAuth } from "react-oidc-context";
import config from "@/config";
import { useEffect, useState } from "react";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import { registerAuth } from "@/lib/auth/authBridge";
import { isPublicRoute } from "@/lib/helpers";

const cognitoAuthConfig: AuthProviderProps = {
  authority: config.cognito.authority,
  client_id: config.cognito.clientId,
  redirect_uri: config.cognito.redirectUri,
  response_type: "code",
  scope: config.cognito.scope,

  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 120,
  // Cognito does not host the OIDC session-monitor iframe; leaving this on
  // can drop the user when the iframe load 404s.
  monitorSession: false,

  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

function TokenSync() {
  const auth = useAuth();
  const setUserToken = useAuthStore((state) => state.setUserToken);

  useEffect(() => {
    registerAuth(auth);
  }, [auth]);

  useEffect(() => {
    if (auth.user?.access_token) {
      setUserToken(auth.user.access_token);
    }
  }, [auth.user?.access_token, setUserToken]);

  useEffect(() => {
    const events = auth.events;
    if (!events) return;

    const onSilentRenewError = (err: Error) => {
      console.error("Silent token renewal failed", err);
    };
    const onUserSignedOut = () => setUserToken(null);
    const onUserUnloaded = () => setUserToken(null);

    events.addSilentRenewError(onSilentRenewError);
    events.addUserSignedOut(onUserSignedOut);
    events.addUserUnloaded(onUserUnloaded);

    return () => {
      events.removeSilentRenewError(onSilentRenewError);
      events.removeUserSignedOut(onUserSignedOut);
      events.removeUserUnloaded(onUserUnloaded);
    };
  }, [auth.events, setUserToken]);

  return null;
}

function getOidcSession(): Record<string, string> {
  const session: Record<string, string> = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith("oidc.") || key === "app_session")) {
      session[key] = sessionStorage.getItem(key)!;
    }
  }
  return session;
}

export function CognitoAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Public routes never require auth — mount anonymously and skip the
    // session-transfer / forced-logout flow entirely.
    if (isPublicRoute()) {
      setReady(true);
      return;
    }

    const isNewTab = !sessionStorage.getItem("app_session");

    if (!isNewTab) {
      // Existing tab — already has session, mount immediately
      setReady(true);
      return;
    }

    // If manual logout happened, skip transfer wait — go straight to login
    if (localStorage.getItem("manual_logout") === "true") {
      setReady(true);
      return;
    }

    // No previous session — first ever visit, go straight to login
    if (!localStorage.getItem("had_session")) {
      setReady(true);
      return;
    }

    // New tab — request session from existing tabs before mounting AuthProvider
    localStorage.setItem("request_session_transfer", Date.now().toString());
    setTimeout(() => localStorage.removeItem("request_session_transfer"), 1000);

    // No tab answered, so the browser was closed and then we force fresh credentials before re-auth so login takes a single attempt.
    const transferTimeout = setTimeout(() => {
      sessionStorage.setItem("app_session", "true");
      sessionStorage.setItem("forcing_logout", "true");
      useBoundStore.getState().signOutRedirect(false);
    }, 800);

    const handleTransfer = (e: StorageEvent) => {
      if (e.key === "session_transfer" && e.newValue) {
        try {
          const session = JSON.parse(e.newValue) as Record<string, string>;
          Object.entries(session).forEach(([key, value]) => {
            sessionStorage.setItem(key, value);
          });

          // Clean up any dangling PKCE/state keys left from signinRedirect
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith("oidc.") && !key.startsWith("oidc.user:")) {
              localStorage.removeItem(key);
            }
          });
        } catch {
          console.error("Failed to parse session transfer");
        }
        clearTimeout(transferTimeout);
        setReady(true);
      }
    };

    window.addEventListener("storage", handleTransfer);
    return () => {
      clearTimeout(transferTimeout);
      window.removeEventListener("storage", handleTransfer);
    };
  }, []);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      // Existing tab responds to new tab's request
      if (e.key === "request_session_transfer" && e.newValue) {
        const session = getOidcSession();
        if (Object.keys(session).length > 0) {
          localStorage.setItem("session_transfer", JSON.stringify(session));
          setTimeout(() => localStorage.removeItem("session_transfer"), 1000);
        }
      }

      // Any tab logged out manually — log this tab out too
      if (e.key === "manual_logout" && e.newValue === "true") {
        sessionStorage.removeItem("app_session");

        // Clear all oidc.user from sessionStorage
        Object.keys(sessionStorage)
          .filter((key) => key.startsWith("oidc."))
          .forEach((key) => sessionStorage.removeItem(key));
        // Don't bounce a public page (e.g. /legal) to login on a cross-tab logout.
        if (!isPublicRoute()) {
          window.location.replace("/login");
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  if (!ready) return null;

  return (
    <AuthProvider {...cognitoAuthConfig}>
      <TokenSync />
      {children}
    </AuthProvider>
  );
}
